import React, { useState, useCallback } from "react";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import { showToastMessage, loadingToast } from "../../../../../../../../utils/commonFunctions";
import { buildCoverPageHtml, buildMissionProfileHtml, buildFaceAnalysisHtml, buildLineChartSvg, buildSubsystemAnalysisHtml, buildSchedulePageHtml, buildSimulationTemperaturesPageHtml, buildAlertsPageHtml } from "./pdfHtmlTemplates";
import type { ScheduleTaskType } from "../../../../../utils/scenario-types";
import { buildPdfData, PdfDataBuilderInput, extractSubsystemTemperature, downsampleTimeSeries, paginateSubsystems, paginateSimTempRows, paginateScheduleTasks, paginateAlerts } from "../../hooks/pdfExportHooks";
import { CHART_MAX_POINTS, SUBSYSTEM_CHART_COLOR, getPdfDeckLabel } from "./pdfExportConstants";
import { INT_ORDER } from "../../hooks/ThermalHeatMapHooks";
import { FACE_ORDER } from "../../constants/thermalConstants";
import { FACE_COLORS } from "../charts/ThermalHeatMapChartContent";
// import ThermalHeatMap3dView, { ThermalHeatMap3dViewHandle } from "../visualization/ThermalHeatMap3dView"; // 3D snapshot capture removed; per-face pages now chart-only

// Subsystem mini-chart size: SVG viewBox and the html2canvas capture box must match, otherwise the capture crops the chart
const MINI_CHART_WIDTH = 350;
const MINI_CHART_HEIGHT = 240;


/* Previous mounted-component approach (remounted on every parent render), kept for reference:
interface PdfExport3dViewProps {
  globalMin: number;
  globalMax: number;
  jumpVersion: number;
  isPlaying: boolean;
  isLastStep: boolean;
  playbackSpeed: number;
  isSynced: boolean;
  isSelectedView: { is3dView: boolean; isChartView: boolean; isMarginsView: boolean };
  thermalModel?: any;
  layerState: any;
  renderingSettings: any;
  currentExposure?: any;
}

interface ThermalPdfRendererProps extends PdfDataBuilderInput, PdfExport3dViewProps {
  onGeneratePdf: (handler: () => Promise<void>) => void;
  scheduleTasks?: ScheduleTaskType[];
}

const ThermalPdfRenderer: React.FC<ThermalPdfRendererProps> = ({
  onGeneratePdf,
  heatmapRows,
  nodesPerFace,
  envParams,
  alerts,
  globalMin,
  globalMax,
  jumpVersion,
  isPlaying,
  isLastStep,
  playbackSpeed,
  isSynced,
  isSelectedView,
  thermalModel,
  layerState,
  renderingSettings,
  currentExposure,
  scenarioResponse,
  selectedGroupSatellite,
  orbitalInsights,
  t0Epoch,
  scheduleTasks = [],
  thresholds,
  sunExposures,
}) => {
  const pdfData = usePdfDataBuilder({
    heatmapRows,
    nodesPerFace,
    envParams,
    alerts,
    thermalModel,
    scenarioResponse,
    selectedGroupSatellite,
    orbitalInsights,
    t0Epoch,
    thresholds,
    sunExposures,
  });

  // const thermalViewRef = useRef<ThermalHeatMap3dViewHandle>(null); // 3D snapshot capture removed; per-face pages now chart-only
  // const [stepIndex, setStepIndex] = useState(0); // 3D snapshot capture removed; per-face pages now chart-only
  // const snapshotApi = use3dSnapshot(thermalViewRef); // 3D snapshot capture removed; per-face pages now chart-only

*/

export interface PdfExportParams extends PdfDataBuilderInput {
  scheduleTasks?: ScheduleTaskType[];
}

const generateThermalPdf = async (params: PdfExportParams) => {
  const { heatmapRows, nodesPerFace, scheduleTasks = [] } = params;
  const tempContainer = document.createElement("div");
  tempContainer.style.position = "fixed";
  tempContainer.style.top = "-10000px";
  tempContainer.style.left = "0";
  tempContainer.style.width = "794px";
  tempContainer.style.zIndex = "-10000";
  tempContainer.style.pointerEvents = "none";

  const coverPageDiv = document.createElement("div");
  const missionProfileDiv = document.createElement("div");
  const chartContainerDiv = document.createElement("div");

  try {
    loadingToast({ loading: true, message: "Generating thermal report..." });
    // Yield to allow the browser to paint the loading toast and button disable state
    // before the heavy synchronous computation begins
    await new Promise(resolve => setTimeout(resolve, 0));

    const pdfData = buildPdfData(params);

    document.body.appendChild(tempContainer);
    tempContainer.appendChild(coverPageDiv);
    tempContainer.appendChild(missionProfileDiv);
    document.body.appendChild(chartContainerDiv);

    // Calculate total pages: 2 fixed (cover + mission profile) + paired face pages + subsystem pages + sim temp pages + alert pages + schedule pages
    const facePageCount = Math.ceil(pdfData.faces.length / 2);
    const subsystemPages = paginateSubsystems(pdfData.subsystems, 4);
    const simTempPages = paginateSimTempRows(pdfData.subsystems);
    const alertPages = pdfData.alerts.length > 0 ? paginateAlerts(pdfData.alerts, 6) : [];
    const scheduleTaskPages = paginateScheduleTasks(scheduleTasks, 14);
    const totalPages = 2 + facePageCount + subsystemPages.length + simTempPages.length + alertPages.length + scheduleTaskPages.length;

    coverPageDiv.innerHTML = buildCoverPageHtml(
      pdfData.metadata,
      pdfData.summary,
      pdfData.environment,
      1,
      totalPages,
      pdfData.metadata.satelliteName,
      pdfData.metadata.startTimeFormatted,
      pdfData.metadata.endTimeFormatted
    );

    // Prepare chart data: one average-temperature line per face/deck (8 lines total)
    // Mirrors the app chart's "multi" mode — average of each face's N1..N9 nodes per time step
    const ALL_KEYS = [...FACE_ORDER, ...INT_ORDER];

    // Compute each face/deck's average-temperature series, reusing pre-computed data where available
    // For FACE_ORDER faces, temps were already computed in usePdfDataBuilder; only compute for INT_ORDER decks
    const facesById = new Map(pdfData.faces.map(f => [f.faceId, f]));
    const faceSeriesMap: Record<string, number[]> = {};
    for (const faceKey of ALL_KEYS) {
      const cached = facesById.get(faceKey)?.temps;
      if (cached) {
        faceSeriesMap[faceKey] = cached;
      } else {
        const nodeIds = Array.from({ length: nodesPerFace }, (_, i) => `${faceKey}_N${i + 1}`);
        faceSeriesMap[faceKey] = extractSubsystemTemperature(nodeIds, heatmapRows);
      }
    }

    // Compute shared Y-axis min/max across all 8 series for consistent chart scaling
    let chartGlobalMin = Infinity, chartGlobalMax = -Infinity;
    for (const series of Object.values(faceSeriesMap)) {
      for (const temp of series) {
        if (temp < chartGlobalMin) chartGlobalMin = temp;
        if (temp > chartGlobalMax) chartGlobalMax = temp;
      }
    }

    // Render chart into the temporary container
    chartContainerDiv.style.position = "fixed";
    chartContainerDiv.style.top = "-10000px";
    chartContainerDiv.style.width = "700px";
    chartContainerDiv.style.height = "380px";
    chartContainerDiv.style.backgroundColor = "white";

    // Build series data for all 8 faces/decks, downsampling to keep chart generation fast
    const chartSeries = ALL_KEYS.map((faceKey) => {
      const points = downsampleTimeSeries(pdfData.times, faceSeriesMap[faceKey], CHART_MAX_POINTS);
      return {
        label: getPdfDeckLabel(faceKey),
        color: FACE_COLORS[faceKey] || "#888",
        points,
      };
    });

    // Use shared SVG chart builder to generate the chart
    const chartHtml = buildLineChartSvg(chartSeries, { width: 700, height: 380, showLegend: true, showLineLabels: false });

    chartContainerDiv.innerHTML = chartHtml;

    await new Promise(resolve => requestAnimationFrame(() => requestAnimationFrame(resolve)));

    // Capture the chart
    const chartCanvas = await html2canvas(chartContainerDiv, {
      scale: 2,
      useCORS: true,
      logging: false,
      backgroundColor: "#ffffff",
      allowTaint: true,
    });

    const chartImageDataUrl = chartCanvas.toDataURL("image/jpeg", 1);
    missionProfileDiv.innerHTML = buildMissionProfileHtml(
      chartImageDataUrl,
      2,
      totalPages,
      1,
      pdfData.metadata.satelliteName,
      pdfData.metadata.startTimeFormatted,
      pdfData.metadata.endTimeFormatted
    );

    await new Promise(resolve => requestAnimationFrame(() => requestAnimationFrame(resolve)));

    // Build paired per-face analysis pages (2 faces per page)
    // 3D snapshot capture removed; per-face pages now chart-only
    const faceAnalysisPages: { element: HTMLDivElement }[] = [];
    let figureCounter = 2; // Figure 1 = Mission Profile, Figures 2+ = per-face pages

    for (let i = 0; i < pdfData.faces.length; i += 2) {
      const facePair = [pdfData.faces[i], pdfData.faces[i + 1]].filter(Boolean); // Handle odd count gracefully
      const figureNumbers = facePair.map(() => figureCounter++);
      const facePageDiv = document.createElement("div");
      const pageNumber = 3 + faceAnalysisPages.length; // Page 3+ = face pages (increments per page, not per face)
      facePageDiv.innerHTML = buildFaceAnalysisHtml(
        facePair,
        pdfData.times,
        figureNumbers,
        pageNumber,
        totalPages,
        pdfData.metadata.satelliteName,
        pdfData.metadata.startTimeFormatted,
        pdfData.metadata.endTimeFormatted
      );
      tempContainer.appendChild(facePageDiv);
      faceAnalysisPages.push({ element: facePageDiv });
    }

    // Build subsystem analysis pages with mini-charts
    const subsystemAnalysisPages: { element: HTMLDivElement }[] = [];
    const startPageNum = 3 + facePageCount; // Start after face pages
    const startingSubsystemFigureNumber = 2 + pdfData.faces.length; // Figure numbering continues after per-face figures

    for (let pageIdx = 0; pageIdx < subsystemPages.length; pageIdx++) {
      const subsystemGroup = subsystemPages[pageIdx];
      const subsystemPageDiv = document.createElement("div");
      const pageNum = startPageNum + pageIdx;

      // Build mini-charts for each subsystem in this page group
      const subsystemsWithCharts = await Promise.all(
        subsystemGroup.map(async (subsystem) => {
          const subsystemTemps = extractSubsystemTemperature([subsystem.subsystemId], heatmapRows);
          const points = downsampleTimeSeries(pdfData.times, subsystemTemps, CHART_MAX_POINTS);

          // Build mini-chart SVG (single series, no legend, sparse X-axis ticks)
          const miniChartSvg = buildLineChartSvg(
            [{ label: subsystem.displayName, color: SUBSYSTEM_CHART_COLOR, points }],
            { width: MINI_CHART_WIDTH, height: MINI_CHART_HEIGHT, showLegend: false, xTickMode: "sparse" }
          );

          // Render SVG to canvas via html2canvas, then embed as JPEG data URL
          const tempChartDiv = document.createElement("div");
          tempChartDiv.innerHTML = miniChartSvg;
          tempChartDiv.style.position = "fixed";
          tempChartDiv.style.top = "-10000px";
          tempChartDiv.style.left = "0";
          // tempChartDiv.style.width = "300px";
          // tempChartDiv.style.height = "220px";
          tempChartDiv.style.width = `${MINI_CHART_WIDTH}px`;
          tempChartDiv.style.height = `${MINI_CHART_HEIGHT}px`;
          document.body.appendChild(tempChartDiv);

          try {
            await new Promise(resolve => requestAnimationFrame(() => requestAnimationFrame(resolve)));

            const chartCanvas = await html2canvas(tempChartDiv, {
              scale: 2,
              useCORS: true,
              logging: false,
              backgroundColor: "#ffffff",
              allowTaint: true,
            });

            const chartImageDataUrl = chartCanvas.toDataURL("image/jpeg", 1);

            return {
              ...subsystem,
              timeseriesChart: chartImageDataUrl,
            };
          } finally {
            if (tempChartDiv.parentNode) {
              document.body.removeChild(tempChartDiv);
            }
          }
        })
      );

      subsystemPageDiv.innerHTML = buildSubsystemAnalysisHtml(
        subsystemsWithCharts,
        pageNum,
        totalPages,
        startingSubsystemFigureNumber + pdfData.subsystems.indexOf(subsystemGroup[0]),
        pdfData.metadata.satelliteName,
        pdfData.metadata.startTimeFormatted,
        pdfData.metadata.endTimeFormatted
      );
      tempContainer.appendChild(subsystemPageDiv);
      subsystemAnalysisPages.push({ element: subsystemPageDiv });
    }

    // Build simulation temperatures analysis pages (with pagination)
    const simTempAnalysisPages: { element: HTMLDivElement }[] = [];
    const simTempStartPageNum = 3 + facePageCount + subsystemPages.length;

    for (let pageIdx = 0; pageIdx < simTempPages.length; pageIdx++) {
      const simTempGroup = simTempPages[pageIdx];
      const simTempPageDiv = document.createElement("div");
      const pageNum = simTempStartPageNum + pageIdx;
      simTempPageDiv.innerHTML = buildSimulationTemperaturesPageHtml(
        simTempGroup,
        pageNum,
        totalPages,
        pdfData.metadata.satelliteName,
        pdfData.metadata.startTimeFormatted,
        pdfData.metadata.endTimeFormatted,
        pageIdx === 0 // only the first sim temp page shows the "Simulation Temperatures" title
      );
      tempContainer.appendChild(simTempPageDiv);
      simTempAnalysisPages.push({ element: simTempPageDiv });
    }

    // Build alert analysis pages (with pagination)
    const alertsAnalysisPages: { element: HTMLDivElement }[] = [];
    const alertStartPageNum = 3 + facePageCount + subsystemPages.length + simTempPages.length;

    for (let pageIdx = 0; pageIdx < alertPages.length; pageIdx++) {
      const alertGroup = alertPages[pageIdx];
      const alertPageDiv = document.createElement("div");
      const pageNum = alertStartPageNum + pageIdx;
      alertPageDiv.innerHTML = buildAlertsPageHtml(
        alertGroup,
        pageNum,
        totalPages,
        pdfData.metadata.satelliteName,
        pdfData.metadata.startTimeFormatted,
        pdfData.metadata.endTimeFormatted,
        pageIdx === 0 // only the first alert page shows the "Alerts" title
      );
      tempContainer.appendChild(alertPageDiv);
      alertsAnalysisPages.push({ element: alertPageDiv });
    }

    // Build schedule analysis pages (with pagination)
    const scheduleAnalysisPages: { element: HTMLDivElement }[] = [];
    const scheduleStartPageNum = 3 + facePageCount + subsystemPages.length + simTempPages.length + alertPages.length;

    for (let pageIdx = 0; pageIdx < scheduleTaskPages.length; pageIdx++) {
      const taskGroup = scheduleTaskPages[pageIdx];
      const schedulePageDiv = document.createElement("div");
      const pageNum = scheduleStartPageNum + pageIdx;
      schedulePageDiv.innerHTML = buildSchedulePageHtml(
        taskGroup,
        pageNum,
        totalPages,
        pdfData.metadata.satelliteName,
        pdfData.metadata.startTimeFormatted,
        pdfData.metadata.endTimeFormatted,
        pageIdx === 0 // only the first schedule page shows the "Schedule" title
      );
      tempContainer.appendChild(schedulePageDiv);
      scheduleAnalysisPages.push({ element: schedulePageDiv });
    }

    const doc = new jsPDF("portrait", "mm", "a4", true);
    const pageElements = [
      { element: coverPageDiv },
      { element: missionProfileDiv },
      ...faceAnalysisPages,
      ...subsystemAnalysisPages,
      ...simTempAnalysisPages,
      ...alertsAnalysisPages,
      ...scheduleAnalysisPages,
    ];

    for (let i = 0; i < pageElements.length; i++) {
      const { element } = pageElements[i];

      const canvas = await html2canvas(element, {
        scale: 2,
        useCORS: true,
        logging: false,
        backgroundColor: "#ffffff",
        allowTaint: true,
        ignoreElements: (elem) => {
          if (elem.tagName === "CANVAS" && elem.getAttribute("data-engine")) {
            return true;
          }
          return elem.tagName === "SCRIPT" || elem.tagName === "STYLE";
        },
      });

      if (canvas.width === 0 || canvas.height === 0) {
        throw new Error(`Canvas has invalid dimensions: ${canvas.width}x${canvas.height}`);
      }

      const imageData = canvas.toDataURL("image/jpeg", 1);

      if (!imageData || imageData.length < 100) {
        throw new Error("Canvas produced invalid or empty image data");
      }

      doc.addImage(imageData, "JPEG", 0, 0, 210, 297);

      if (i < pageElements.length - 1) {
        doc.addPage("a4", "portrait");
      }
    }

    const pdfBlob = doc.output("blob");
    const pdfUrl = URL.createObjectURL(pdfBlob);

    const newWindow = window.open(pdfUrl, "_blank");
    if (!newWindow) {
      showToastMessage("error", "Failed to open PDF preview. Please check your popup blocker settings.");
    } else {
      showToastMessage("success", "Thermal report PDF generated successfully");
    }
  } catch (error) {
    console.error("Error during PDF generation:", error);
    const errorMessage = error instanceof Error ? error.message : String(error);
    showToastMessage("error", `Failed to generate PDF: ${errorMessage}`);
  } finally {
    loadingToast({ loading: false, message: "" });
    if (tempContainer.parentNode) {
      document.body.removeChild(tempContainer);
    }
    if (chartContainerDiv.parentNode) {
      document.body.removeChild(chartContainerDiv);
    }
  }
};

/*
  // Old handler registration from ThermalPdfRenderer:

  React.useEffect(() => {
    onGeneratePdf(generatePdf);
  }, [generatePdf, onGeneratePdf, pdfData, heatmapRows, nodesPerFace, scheduleTasks, thresholds]);

  // 3D snapshot capture removed; per-face pages now chart-only
  // Render the 3D view off-screen for snapshot capture (COMMENTED OUT)
  // It's mounted but hidden with negative positioning
  // return (
  //   <div style={{ position: "fixed", top: "-10000px", left: "0", width: "800px", height: "600px", zIndex: "-10000", pointerEvents: "none" }}>
  //     <ThermalHeatMap3dView
  //       ref={thermalViewRef}
  //       heatmapRows={heatmapRows}
  //       stepIndex={stepIndex}
  //       jumpVersion={0}
  //       nodesPerFace={nodesPerFace}
  //       globalMin={globalMin}
  //       globalMax={globalMax}
  //       isPlaying={false}
  //       isLastStep={false}
  //       playbackSpeed={1}
  //       isSynced={false}
  //       viewMode="normal"
  //       isSelectedView={{ is3dView: true, isChartView: false, isMarginsView: false }}
  //       thermalModel={thermalModel}
  //       layerState={layerState}
  //       renderingSettings={{ ...renderingSettings, refreshRateMs: 0 } as any}
  //       currentExposure={currentExposure}
  //       onToggleSync={() => {}}
  //       onTogglePlay={() => {}}
  //       onCycleSpeed={() => {}}
  //       onViewChange={() => {}}
  //     />
  //   </div>
  // );
  return null;
};
*/

export const useThermalPdfExport = (getParams: () => PdfExportParams | null) => {
  const [isGenerating, setIsGenerating] = useState(false);

  const trigger = useCallback(async () => {
    const params = getParams();
    if (!params) {
      showToastMessage("error", "Thermal data not ready. Please try again.");
      return;
    }
    setIsGenerating(true);
    try {
      await generateThermalPdf(params);
    } finally {
      setIsGenerating(false);
    }
  }, [getParams]);

  return { trigger, isGenerating };
};
